import { useEffect, useState } from "react"
import { StepProps } from "../../../../components/Stepper"
import Payments from "../../../../features/buy/Payments"
import { userStore } from "../../../../store/userStore"
import { BuyData } from "../../../../types/steps/buy"
import { Place } from "../../../../types/entities/session"
import { useRequest } from "../../../../hooks/useRequest"
import { appStore } from "../../../../store/appStore"
import BonusSpendBlock from "../../../../features/buy/BonusSpendBlock"
import { useIsKeyboardOpen } from "../../../../hooks/useIsKeyboardOpen"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

const SESSION_META = [
    { key: "cinema", label: "Кинотеатр" },
    { key: "hall", label: "Зал" },
    { key: "format", label: "Формат" },
    { key: "datetime", label: "Сеанс" },
] as const

export default function Pay({ params, navigate }: StepProps<BuyData>) {
    const { payInfo } = params
    const { selectedSeats, totalPrice, film_name, format, other } = payInfo
    const [bonusPay, setBonusPay] = useState(0)
    const { user, setUser } = userStore()
    const { city } = appStore()
    const { request } = useRequest()
    const isKeyboardOpen = useIsKeyboardOpen()
    const card = user?.cards.find(item => !item.blocked)
    const balance = card?.movie.balance || 0
    const percents = card?.movie.percents || 0

    const updateUserData = async () => {
        if (!user) return
        const data = await request("GET", `user?city_id=${city.id}`)
        setUser(data)
    }

    useEffect(() => {
        updateUserData()
    }, [city.id, user?.id])

    const orderBody = {
        session_id: payInfo.selectedSession,
        seats: payInfo.selectedSeats.map(item => item.id),
        bonus_pay: bonusPay,
        phone: user?.phone,
        email: user?.email
    }

    function groupSeatsByRow(seats: Place[]) {
        return Object.values(
            seats.reduce<Record<number, { row: number; places: number[]; price: number }>>((acc, seat) => {
                if (!acc[seat.row]) {
                    acc[seat.row] = { row: seat.row, places: [], price: 0 }
                }

                acc[seat.row].places.push(seat.place)
                acc[seat.row].price += seat.price
                return acc
            }, {})
        )
    }

    const sessionMeta = {
        cinema: other.cinemaName ?? "—",
        hall: other.hallNumber ?? "—",
        format: format.toUpperCase(),
        datetime: other.date && other.time ? `${other.date}, ${other.time}` : other.date ?? "—",
    }

    return (
        <div className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col gap-8 overflow-x-hidden overflow-y-auto overscroll-x-none p-5">
            <div className="flex flex-col gap-3 border-b border-white/10 pb-5">
                <div className="flex min-w-0 items-start gap-2.5">
                    <h3
                        className="min-w-0 text-white leading-tight"
                        style={{
                            fontFamily: DISPLAY,
                            fontWeight: 800,
                            fontSize: "18px",
                            letterSpacing: "-0.02em",
                        }}>
                        {film_name}
                    </h3>
                </div>
                <div className="grid w-full max-w-full grid-cols-4 gap-x-3 gap-y-1 sm:gap-x-5">
                    {SESSION_META.map(item => (
                        <span
                            key={`label-${item.key}`}
                            className="text-[10px] uppercase tracking-[0.06em] text-white/40"
                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                            {item.label}
                        </span>
                    ))}
                    {SESSION_META.map(item => (
                        <span
                            key={`value-${item.key}`}
                            className="min-w-0 text-[12px] leading-snug text-white/85"
                            style={{ fontFamily: BODY, fontWeight: 600 }}>
                            {sessionMeta[item.key]}
                        </span>
                    ))}
                </div>
            </div>
            <div className="flex flex-col gap-2.5">
                <div
                    className="text-[12px] uppercase tracking-[0.06em] text-white/45"
                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                    Места
                </div>
                {groupSeatsByRow(selectedSeats).map((item, index) => {
                    const placesLabel = item.places.length > 1 ? "места" : "место"
                    const placesText = item.places.slice().sort((a, b) => a - b).join(", ")

                    return (
                        <div key={index} className="flex flex-row items-center justify-between gap-3">
                            <div
                                className="flex shrink-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[13px] leading-snug text-white/50"
                                style={{ fontFamily: BODY, fontWeight: 500 }}>
                                <span className="whitespace-nowrap">
                                    ряд{" "}
                                    <span className="text-white" style={{ fontFamily: MONO, fontWeight: 700 }}>
                                        {item.row}
                                    </span>
                                </span>
                                <span className="text-white/25">|</span>
                                <span className="whitespace-nowrap">
                                    {placesLabel}{" "}
                                    <span className="text-white" style={{ fontFamily: MONO, fontWeight: 700 }}>
                                        {placesText}
                                    </span>
                                </span>
                            </div>
                            <div className="min-w-3 flex-1 border-b border-dashed border-white/15" />
                            <div
                                className="shrink-0 text-[13px] tabular-nums text-white"
                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                {item.price.toLocaleString("ru-RU", {
                                    currency: "RUB",
                                    style: "currency",
                                    maximumFractionDigits: 0,
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
            <BonusSpendBlock
                balance={balance}
                totalPrice={totalPrice}
                seatCount={selectedSeats.length}
                percents={percents}
                bonusPay={bonusPay}
                onBonusPayChange={setBonusPay}
            />
            <div className={`mt-auto flex flex-col gap-4 border-t border-white/10 pt-6 ${isKeyboardOpen ? "pb-1" : "pb-[max(0px,var(--safe-bottom))]"}`}>
                <div
                    className="flex items-baseline justify-between gap-4 text-[20px] leading-tight text-white"
                    style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: "-0.02em" }}>
                    <span>Итого:</span>
                    <span className="shrink-0 tabular-nums">
                        {(totalPrice - bonusPay).toLocaleString("ru-RU", {
                            currency: "RUB",
                            style: "currency",
                            maximumFractionDigits: 0,
                        })}
                    </span>
                </div>
                <Payments navigate={navigate} payInfo={payInfo} orderBody={orderBody} />
            </div>
        </div>
    )
}
